import type { ReactNode } from 'react';

import { Button, Skeleton } from '@/components/ui';
import type {
  TaskReadoutState,
  TaskStateFilter,
  TaskStatusResponse,
} from '@/lib/admin/client';
import { deriveTaskReadouts, taskReadoutLabel } from '@/lib/admin/taskReadout';

export type TaskOverviewProps = {
  data: TaskStatusResponse | null;
  isLoading: boolean;
  isError: boolean;
  isDisabled: boolean;
  onStateFilterChange: (filter: TaskStateFilter) => void;
};

export function TaskOverview({
  data,
  isLoading,
  isError,
  isDisabled,
  onStateFilterChange,
}: TaskOverviewProps) {
  if (isLoading) {
    return <Skeleton className="h-24 w-full" />;
  }
  if (isError) {
    return (
      <p className="text-destructive text-sm">
        Task overview could not be loaded.
      </p>
    );
  }
  if (isDisabled || !data) return null;

  const stateCounts = new Map<TaskReadoutState, number>();
  const taskReadouts = deriveTaskReadouts(data);
  let cost = 0;
  let inputTokens = 0;
  let outputTokens = 0;
  for (const attempt of data.attempts) {
    cost += attempt.costUsd;
    inputTokens += attempt.inputTokens;
    outputTokens += attempt.outputTokens;
  }
  for (const readout of taskReadouts.values()) {
    stateCounts.set(readout.state, (stateCounts.get(readout.state) ?? 0) + 1);
  }
  const countStates = (states: TaskReadoutState[]) =>
    states.reduce((sum, state) => sum + (stateCounts.get(state) ?? 0), 0);
  const stateDetail = (states: TaskReadoutState[]) =>
    states.flatMap((state) => {
      const count = stateCounts.get(state) ?? 0;
      const label = taskReadoutLabel(state);
      return count > 0 ? (
        <Button
          key={state}
          type="button"
          variant="ghost"
          className="h-auto p-0 text-xs"
          aria-label={`Show ${label.toLowerCase()} tasks`}
          data-stat-segment={state}
          onClick={() => onStateFilterChange(state)}
        >
          {count} {label.toLowerCase()}
        </Button>
      ) : (
        []
      );
    });
  return (
    <section
      aria-label="Task Overview"
      className="border-border bg-background w-full overflow-hidden rounded-lg border"
    >
      <div className="bg-border grid sm:grid-cols-2 lg:w-fit lg:grid-cols-[repeat(6,minmax(8rem,10rem))_minmax(14rem,18rem)]">
        <OverviewMetric
          title="Running"
          value={stateCounts.get('running') ?? 0}
          ariaLabel="Show running tasks"
          onSelect={() => onStateFilterChange('running')}
        />
        <OverviewMetric
          title="Retrying"
          value={stateCounts.get('retrying') ?? 0}
          ariaLabel="Show retrying tasks"
          onSelect={() => onStateFilterChange('retrying')}
        />
        <OverviewMetric
          title="Ready"
          value={stateCounts.get('ready') ?? 0}
          ariaLabel="Show ready tasks"
          onSelect={() => onStateFilterChange('ready')}
        />
        <OverviewMetric
          title="Needs attention"
          value={stateCounts.get('blocked') ?? 0}
          ariaLabel="Show blocked tasks"
          onSelect={() => onStateFilterChange('blocked')}
        />
        <OverviewMetric
          title="Finished"
          value={countStates(['done', 'delivered'])}
          ariaLabel="Show done tasks"
          onSelect={() => onStateFilterChange('done')}
          detail={stateDetail(['done', 'delivered'])}
        />
        <OverviewMetric title="Cost" value={`$${cost.toFixed(2)}`} />
        <OverviewMetric
          title="Tokens"
          value={`${outputTokens.toLocaleString('en-US')} output`}
          detail={
            <span data-stat-segment="input">
              {inputTokens.toLocaleString('en-US')} input
            </span>
          }
        />
      </div>
    </section>
  );
}

function OverviewMetric({
  title,
  value,
  detail,
  ariaLabel,
  onSelect,
}: {
  title: string;
  value: number | string;
  detail?: ReactNode;
  ariaLabel?: string;
  onSelect?: () => void;
}) {
  const valueNode = (
    <span className="text-xl font-semibold" data-stat-title={title}>
      {value}
    </span>
  );
  return (
    <div className="bg-background min-w-0 px-4 py-3">
      <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
        {title}
      </p>
      {onSelect ? (
        <Button
          type="button"
          variant="ghost"
          className="mt-1 h-auto p-0"
          aria-label={ariaLabel}
          onClick={onSelect}
        >
          {valueNode}
        </Button>
      ) : (
        <div className="mt-1">{valueNode}</div>
      )}
      {detail && (
        <div className="text-muted-foreground mt-1 flex flex-wrap gap-x-3 text-xs">
          {detail}
        </div>
      )}
    </div>
  );
}
